"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { DEFAULT_API_ENDPOINT, DEFAULT_POLLING_INTERVAL } from "../constants";
import type {
  LiveStreamInfo,
  LiveStreamResponse,
  UseYouTubeLiveOptions,
  UseYouTubeLiveResult,
} from "../types";

/**
 * React hook for monitoring YouTube channel live status
 *
 * Polls an API endpoint at a configurable interval to check if a channel
 * is currently live streaming. Provides callbacks for live/offline state changes.
 *
 * @param options - Configuration options for the hook
 * @returns Live status, stream info, loading state, and control functions
 *
 * @example
 * ```tsx
 * function LiveAlert() {
 *   const { isLive, stream, isLoading } = useYouTubeLive({
 *     pollingInterval: 60000,
 *     onLive: (stream) => console.log('Now live:', stream.title),
 *     onOffline: () => console.log('Stream ended'),
 *   });
 *
 *   if (!isLive) return null;
 *
 *   return (
 *     <div>
 *       <h2>{stream?.title}</h2>
 *       <a href={stream?.watchUrl}>Watch now</a>
 *     </div>
 *   );
 * }
 * ```
 */
export function useYouTubeLive(
  options: UseYouTubeLiveOptions = {},
): UseYouTubeLiveResult {
  const {
    channelId,
    pollingInterval = DEFAULT_POLLING_INTERVAL,
    enabled = true,
    onLive,
    onOffline,
    apiEndpoint = DEFAULT_API_ENDPOINT,
  } = options;

  const [isLive, setIsLive] = useState(false);
  const [stream, setStream] = useState<LiveStreamInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);

  // Track previous live state to detect transitions
  const wasLiveRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Store callbacks in refs to avoid effect dependencies
  const onLiveRef = useRef(onLive);
  const onOfflineRef = useRef(onOffline);
  onLiveRef.current = onLive;
  onOfflineRef.current = onOffline;

  const fetchLiveStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Build URL with optional channel ID
      const url = new URL(apiEndpoint, globalThis.location?.origin ?? "");
      if (channelId) {
        url.searchParams.set("channelId", channelId);
      }

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: LiveStreamResponse = await response.json();

      if (!data.success) {
        throw new Error(data.error ?? "Unknown error");
      }

      const nowLive = data.data.isLive;
      const streamInfo = data.data.stream ?? null;

      setIsLive(nowLive);
      setStream(streamInfo);

      // Handle state transitions
      if (nowLive && !wasLiveRef.current && streamInfo) {
        onLiveRef.current?.(streamInfo);
      } else if (!nowLive && wasLiveRef.current) {
        onOfflineRef.current?.();
      }

      wasLiveRef.current = nowLive;
    } catch (error_) {
      const message =
        error_ instanceof Error ? error_.message : "Unknown error";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [apiEndpoint, channelId]);

  const pause = useCallback(() => {
    setIsPaused(true);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const resume = useCallback(() => {
    setIsPaused(false);
  }, []);

  const refresh = useCallback(async () => {
    await fetchLiveStatus();
  }, [fetchLiveStatus]);

  // Initial fetch and polling setup
  useEffect(() => {
    if (!enabled || isPaused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Initial fetch
    void fetchLiveStatus();

    // Set up polling
    intervalRef.current = setInterval(() => {
      void fetchLiveStatus();
    }, pollingInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, isPaused, pollingInterval, fetchLiveStatus]);

  return {
    isLive,
    stream,
    isLoading,
    error,
    refresh,
    pause,
    resume,
    isPaused,
  };
}
